import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Activity, ArrowUpRight } from 'lucide-react'
import { api } from '../api'
import type { Task } from '../tasks'
import { go } from '../router'
import { Button, StatusBadge } from './ui'
import { cn, usd, ago } from '../lib/utils'

// Shows work in progress from anywhere in the app.
//
// The queue and the reload fix both assume you come back to the page that
// started the work. This is for when you do not: start a generation, navigate
// to Brands, and there was previously nothing telling you it was still going
// -- or that it had finished, or failed.

const ACTIVE = new Set(['queued', 'running'])

const KIND_LABEL: Record<string, string> = {
  generate_avatar: 'AI avatar',
  generate_plates: 'Avatar plates',
  generate_look: 'Look start frame',
  extract_brand: 'Brand context',
  draft_script: 'Script draft',
}

export function TaskIndicator({ ws }: { ws: string }) {
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const tasks = useQuery({
    queryKey: ['tasks', ws],
    queryFn: () => api.get<Task[]>(`/api/workspaces/${ws}/tasks`),
    // Tight while something is live so a finish is noticed promptly; slow
    // otherwise, because this polls for every user on every screen.
    refetchInterval: q => {
      const data = q.state.data as Task[] | undefined
      return data?.some(t => ACTIVE.has(t.status)) ? 4000 : 30_000
    },
  })

  const cancel = useMutation({
    mutationFn: (id: string) => api.post(`/api/workspaces/${ws}/tasks/${id}/cancel`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tasks', ws] }),
  })

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  const all = tasks.data ?? []
  const active = all.filter(t => ACTIVE.has(t.status))
  if (all.length === 0) return null

  const recentFailure = all.find(
    t => t.status === 'failed' && Date.now() - new Date(t.created_at).getTime() < 3_600_000,
  )

  return (
    <div className="relative" ref={ref}>
      <Button
        variant="ghost" size="sm"
        onClick={() => setOpen(v => !v)}
        className={cn(
          active.length ? 'text-warn' : recentFailure ? 'text-bad' : 'text-ink-faint',
        )}
      >
        {active.length > 0 ? (
          <span className="pulse-dot size-1.5 rounded-full bg-current" />
        ) : (
          <Activity />
        )}
        {active.length > 0
          ? `${active.length} running`
          : recentFailure ? '1 failed' : 'Activity'}
      </Button>

      {open && (
        <div className="panel absolute right-0 top-11 z-50 w-[26rem] max-w-[90vw] p-2">
          <div className="px-2 pb-2 pt-1 text-[11px] font-medium uppercase tracking-[0.08em] text-ink-faint">
            Recent generation
          </div>

          <div className="max-h-80 space-y-0.5 overflow-y-auto">
            {all.slice(0, 12).map(t => (
              <div
                key={t.id}
                className="flex items-center gap-2.5 rounded-[--radius-control] px-2 py-2 hover:bg-surface-2"
              >
                <StatusBadge status={t.status} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[13px]">{KIND_LABEL[t.kind] ?? t.kind}</div>
                  {t.error
                    ? <div className="truncate text-[11px] text-bad" title={t.error}>{t.error}</div>
                    : <div className="text-[11px] text-ink-faint">{ago(t.created_at)}</div>}
                </div>
                {t.cost_cents > 0 && (
                  <span className="shrink-0 text-xs tabular-nums text-ink-faint">
                    {usd(t.cost_cents)}
                  </span>
                )}
                {t.status === 'queued' && (
                  <Button variant="ghost" size="sm" onClick={() => cancel.mutate(t.id)}>Cancel</Button>
                )}
                {t.status === 'done' && t.kind === 'generate_look' && (
                  <Button
                    variant="ghost" size="icon"
                    onClick={() => { setOpen(false); go({ name: 'settings', section: 'avatars' }) }}
                    title="View"
                  >
                    <ArrowUpRight />
                  </Button>
                )}
              </div>
            ))}
          </div>

          <p className="border-t border-line px-2 pt-2 text-[11px] leading-relaxed text-ink-faint">
            Only queued work can be cancelled — anything already running has been paid for at
            the provider.
          </p>
        </div>
      )}
    </div>
  )
}
